import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase/config';
import { collection, query, where, orderBy, onSnapshot, addDoc, Timestamp, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { Send, User, MessageSquare, ShieldCheck } from 'lucide-react';

interface MessageBoardProps {
  clientId: string;
  lawyerId: string;
}

const MessageBoard: React.FC<MessageBoardProps> = ({ clientId, lawyerId }) => {
  const { user, role } = useAuth();
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [clientInfo, setClientInfo] = useState<any>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const threadId = [clientId, lawyerId].sort().join('_');

  useEffect(() => {
    const fetchClientInfo = async () => {
      const snap = await getDoc(doc(db, 'users', clientId));
      if (snap.exists()) setClientInfo(snap.data());
    };
    fetchClientInfo();

    const q = query(
      collection(db, 'messages'),
      where('threadId', '==', threadId),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMessages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsubscribe();
  }, [threadId, clientId]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !user) return;

    try {
      await addDoc(collection(db, 'messages'), {
        threadId,
        senderId: user.uid,
        senderEmail: user.email,
        text: newMessage,
        createdAt: Timestamp.now()
      });
      setNewMessage('');
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '600px', 
      backgroundColor: 'white', 
      borderRadius: '12px', 
      boxShadow: 'var(--shadow)',
      overflow: 'hidden',
      border: '1px solid #eee'
    }}>
      {/* Header */}
      <div style={{ padding: '15px 20px', backgroundColor: 'var(--primary-color)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ backgroundColor: 'rgba(255,255,255,0.2)', padding: '8px', borderRadius: '50%' }}>
            <User size={20} />
          </div>
          <div>
            <div style={{ fontWeight: 'bold', fontSize: '1rem' }}>
              {role === 'lawyer' ? clientInfo?.email || 'Client' : 'Your Legal Advisor'}
            </div>
            <div style={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '4px', opacity: 0.8 }}>
              <ShieldCheck size={12} /> Encrypted Communication
            </div>
          </div>
        </div>
        <MessageSquare size={20} />
      </div>

      {/* Messages Area */}
      <div style={{ flex: 1, padding: '20px', overflowY: 'auto', backgroundColor: '#f9f9f9' }}>
        {messages.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#999', marginTop: '40px' }}>
            Starting a secure conversation with your legal professional...
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {messages.map((msg) => (
              <div key={msg.id} style={{
                alignSelf: msg.senderId === user?.uid ? 'flex-end' : 'flex-start',
                maxWidth: '75%',
              }}>
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '12px',
                  backgroundColor: msg.senderId === user?.uid ? 'var(--secondary-color)' : 'white',
                  color: msg.senderId === user?.uid ? 'white' : '#333',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                  fontSize: '0.9rem',
                  border: msg.senderId === user?.uid ? 'none' : '1px solid #eee'
                }}>
                  {msg.text}
                </div>
                <div style={{ 
                  fontSize: '0.65rem', 
                  color: '#999', 
                  marginTop: '4px', 
                  textAlign: msg.senderId === user?.uid ? 'right' : 'left' 
                }}>
                  {msg.createdAt?.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
            <div ref={scrollRef} />
          </div>
        )}
      </div>

      {/* Input Area */}
      <form onSubmit={handleSendMessage} style={{ padding: '20px', borderTop: '1px solid #eee', display: 'flex', gap: '10px' }}>
        <label htmlFor="message-board-input" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}>
          Type your message
        </label>
        <input 
          id="message-board-input"
          name="message"
          type="text" 
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type your message here..."
          style={{ 
            flex: 1, 
            padding: '12px 16px', 
            borderRadius: '25px', 
            border: '1px solid #ddd',
            outline: 'none',
            fontSize: '0.9rem'
          }}
        />
        <button 
          type="submit" 
          aria-label="Send message"
          style={{ 
            backgroundColor: 'var(--primary-color)', 
            color: 'white', 
            width: '45px', 
            height: '45px', 
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            transition: 'var(--transition)'
          }}
        >
          <Send size={18} />
        </button>
      </form>
    </div>
  );
};

export default MessageBoard;
